import { Monitor, Laptop, Apple, Network } from "lucide-react";

// Single source of truth for the core service chapters + their detail pages.
export const SERVICES = [
  {
    slug: "computer-repair",
    num: "01",
    icon: Monitor,
    title: "Computer Repair",
    desc: "Desktop diagnostics, hardware replacement, upgrades and software troubleshooting. Custom rigs, gaming PCs, motherboard chip-level repair and PSU faults.",
    tags: ["Chip-level board repair", "PSU & GPU faults", "Custom builds"],
    eta: "Same day – 48h",
    hero: "/assets/collateral-repair.jpg",
    tagline: "Desktops brought back to life — from no-boot faults to full custom builds.",
    intro:
      "Whether it's an everyday office tower, an all-in-one, or a high-end gaming rig, our Kembangan bench handles the full spectrum of desktop faults. We diagnose first, explain clearly, and only repair what needs repairing.",
    handles: [
      "No power / no display / no POST diagnostics",
      "Motherboard chip-level & component repair",
      "Power supply (PSU), GPU and cooling faults",
      "Storage upgrades (SSD/NVMe) & clean OS installs",
      "Custom & gaming PC builds and tuning",
      "Blue-screen, crashing and overheating fixes",
    ],
    steps: [
      { t: "Diagnose", d: "Full hardware + software check to pinpoint the real cause, not just the symptom." },
      { t: "Quote", d: "Clear, no-surprise pricing and options before any work begins." },
      { t: "Repair", d: "Genuine parts, careful workmanship, tested under load." },
      { t: "Handover", d: "Verified working, backed by our 90-day workmanship warranty." },
    ],
  },
  {
    slug: "laptop-repair",
    num: "02",
    icon: Laptop,
    title: "Laptop Repair",
    desc: "Practical support for performance, startup, charging, display and hardware issues. Hinges, screens, keyboards, trackpads and water damage recovery.",
    tags: ["Screen & hinge", "Charging faults", "Liquid damage"],
    eta: "Same day – 24h",
    hero: "/assets/collateral-repair.jpg",
    tagline: "Cracked screen, dead battery, won't charge? Most laptops fixed same day.",
    intro:
      "Laptops take a beating — drops, spills, worn hinges and swollen batteries. We repair all major brands with quality parts and give you an honest call on repair vs. replace.",
    handles: [
      "Cracked / dim / flickering screen replacement",
      "Charging port, DC jack and battery replacement",
      "Keyboard, trackpad and hinge repairs",
      "Liquid-damage cleaning & board-level recovery",
      "Slow performance, startup & Windows issues",
      "Data backup before and after repair",
    ],
    steps: [
      { t: "Diagnose", d: "Pinpoint the fault — screen, board, battery or software." },
      { t: "Quote", d: "Upfront price with genuine or grade-A part options." },
      { t: "Repair", d: "Precision teardown and reassembly on the workshop bench." },
      { t: "Handover", d: "Stress-tested and returned with a 90-day warranty." },
    ],
  },
  {
    slug: "mac-repair",
    num: "03",
    icon: Apple,
    title: "Mac Repair",
    desc: "MacBook and iMac troubleshooting, setup and repair support for common faults. M1/M2/M3 logic board diagnostics and battery replacement.",
    tags: ["Logic board", "Retina screens", "Battery & SSD"],
    eta: "24h – 72h",
    hero: "/assets/collateral-repair.jpg",
    tagline: "MacBook & iMac specialists — logic board, battery and Retina display repair.",
    intro:
      "From MacBook Air to iMac and the latest Apple Silicon, we handle setup, upgrades and board-level repair. If it's stuck on the loading bar, won't charge or the screen is cracked, bring it in.",
    handles: [
      "Apple Silicon (M1/M2/M3) & Intel diagnostics",
      "Logic board & component-level repair",
      "Retina / Liquid Retina display replacement",
      "Battery, keyboard and trackpad replacement",
      "macOS reinstall, migration & setup",
      "Liquid-damage assessment & recovery",
    ],
    steps: [
      { t: "Diagnose", d: "Careful Apple-specific diagnostics with the right tools." },
      { t: "Quote", d: "Transparent pricing and a repair-vs-replace recommendation." },
      { t: "Repair", d: "Meticulous work with quality-matched components." },
      { t: "Handover", d: "Fully tested, set up and covered by our warranty." },
    ],
  },
  {
    slug: "it-support-setup",
    num: "04",
    icon: Network,
    title: "IT Support & Setup",
    desc: "System reinstall, setup, software support and practical IT assistance for home or business. Wi-Fi mesh, printers, NAS backup and office networks.",
    tags: ["Office networks", "Wi-Fi & printers", "Data backup"],
    eta: "On-site / remote",
    hero: "/assets/collateral-repair.jpg",
    tagline: "Home & small-business IT — networks, backups and setup that just work.",
    intro:
      "Keep your home or office running smoothly. We set up devices, secure your network, configure backups and provide practical, jargon-free support — on-site around Kembangan or remotely.",
    handles: [
      "Wi-Fi mesh, routers and dead-spot fixes",
      "Printer, NAS and shared-drive setup",
      "New PC/Mac setup & data migration",
      "Automatic backup & recovery plans",
      "Software installation & troubleshooting",
      "Small-office network & security basics",
    ],
    steps: [
      { t: "Assess", d: "Understand your setup and what you need it to do." },
      { t: "Plan", d: "Recommend the simplest, most reliable solution." },
      { t: "Set up", d: "Configure everything on-site or remotely." },
      { t: "Support", d: "Ongoing help whenever something needs a hand." },
    ],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
